import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import TechLogo from '../TechLogo';
import { renderInline } from '../ContentBlocks';
import { getTechnology, interviewQuestionCount } from '../../data/learn';
import { blogPosts } from '../../data/blogPosts';

const trackSlugs = ['javascript', 'react', 'nodejs', 'java', 'dsa'];
const QUESTIONS_PER_TRACK = 5;

// One question from each lesson, so every tab covers a spread of topics.
const buildTrack = (slug) => {
  const tech = getTechnology(slug);
  const all = tech.lessons.flatMap((lesson) =>
    lesson.content
      .filter((block) => block.type === 'qa')
      .map((block) => ({ ...block, lesson }))
  );
  const picked = [];
  tech.lessons.forEach((lesson) => {
    const first = all.find((q) => q.lesson === lesson);
    if (first && picked.length < QUESTIONS_PER_TRACK) picked.push(first);
  });
  return { tech, total: all.length, questions: picked };
};

const rounds = [
  { icon: TimerOutlinedIcon, title: 'Online assessment', text: 'Two or three timed problems. Learn the patterns, not individual problems.' },
  { icon: PsychologyOutlinedIcon, title: 'Problem solving', text: 'Think aloud, start with brute force, then optimise and test edge cases.' },
  { icon: DashboardCustomizeOutlinedIcon, title: 'Machine coding', text: 'Build a working feature in 60–90 minutes. Clean structure beats extra features.' },
  { icon: HubOutlinedIcon, title: 'System design', text: 'Clarify requirements, estimate scale, then design and discuss trade-offs.' },
  { icon: RecordVoiceOverOutlinedIcon, title: 'Behavioural', text: 'Short STAR stories about impact, ownership and handling conflict.' }
];

const guides = blogPosts.filter((p) => ['Interview Tips', 'DSA', 'System Design', 'Career'].includes(p.category)).slice(0, 3);

const QuestionCard = ({ item, tech, open, onToggle, index }) => {
  const answer = Array.isArray(item.answer) ? item.answer : [item.answer];
  return (
    <div className={`iq-card ${open ? 'is-open' : ''}`} style={{ '--i': index }}>
      <button type="button" className="iq-question" onClick={onToggle} aria-expanded={open}>
        <span className="iq-num">Q{index + 1}</span>
        <span className="iq-text">{renderInline(item.question)}</span>
        <AddIcon className="iq-icon" />
      </button>
      <div className="iq-answer-wrap">
        <div className="iq-answer">
          {answer.map((para, i) => <p key={i}>{renderInline(para)}</p>)}
          {item.code && <pre><code>{item.code}</code></pre>}
          <Link to={`/learn/${tech.slug}/${item.lesson.slug}`} className="iq-more">
            Read the full lesson: {item.lesson.title} <ArrowForwardIcon sx={{ fontSize: 15 }} />
          </Link>
        </div>
      </div>
    </div>
  );
};

const InterviewPrep = () => {
  const tracks = useMemo(() => trackSlugs.map(buildTrack), []);
  // The full question bank is a separate chunk; load it after mount just for the counts
  const [bank, setBank] = useState(null);
  const [active, setActive] = useState(0);
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    import('../../data/interview')
      .then((m) => { if (!cancelled) setBank(m); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const trackTotal = ({ tech, total }) => total + (bank?.getInterviewTrack(tech.slug)?.questions.length ?? 0);
  const questionTotal = bank ? bank.deepQuestionCount + bank.rapidFireCount : interviewQuestionCount;
  const track = tracks[active];

  const selectTrack = (i) => {
    setActive(i);
    setOpenIndex(0);
  };

  return (
    <section className="home-section interview-prep" id="interview-prep">
      <div className="container">
        <div className="home-section-head" data-reveal>
          <span className="section-eyebrow">Interview Prep</span>
          <h2>Practise the questions interviewers actually ask</h2>
          <p>
            {questionTotal}+ real interview questions with in-depth answers, a playbook for every round,
            and mock interviews with senior engineers when you are ready.
          </p>
        </div>

        <div className="iq-tabs" role="tablist" aria-label="Interview question tracks" data-reveal>
          {tracks.map((t, i) => (
            <button
              type="button"
              role="tab"
              key={t.tech.slug}
              aria-selected={active === i}
              className={`iq-tab ${active === i ? 'active' : ''}`}
              onClick={() => selectTrack(i)}
            >
              <TechLogo tech={t.tech} size={22} />
              {t.tech.name}
              <span className="iq-tab-count">{trackTotal(t)}</span>
            </button>
          ))}
        </div>

        <div className="iq-layout">
          <div data-reveal>
            <div className="iq-list" key={track.tech.slug}>
              {track.questions.map((item, i) => (
                <QuestionCard
                  key={item.question}
                  item={item}
                  tech={track.tech}
                  index={i}
                  open={openIndex === i}
                  onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                />
              ))}
              <Link to={`/interview-prep/${track.tech.slug}`} className="iq-all">
                See all {trackTotal(track)} {track.tech.name} questions, answered in depth <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Link>
            </div>
          </div>

          <aside className="iq-side">
            <div className="iq-rounds spotlight" data-reveal>
              <h3>Know every round</h3>
              <ol>
                {rounds.map(({ icon: Icon, title, text }, i) => (
                  <li key={title} style={{ '--i': i }}>
                    <span className="iq-round-icon"><Icon sx={{ fontSize: 20 }} /></span>
                    <div>
                      <strong>{title}</strong>
                      <p>{text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="iq-guides spotlight" data-reveal>
              <h3>Interview guides</h3>
              {guides.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.slug} className="iq-guide">
                  <span>{post.category}</span>
                  {post.title}
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Link>
              ))}
            </div>
          </aside>
        </div>

        <div className="iq-mock" data-reveal>
          <span className="iq-mock-icon"><RecordVoiceOverIcon /></span>
          <div>
            <h3>Ready for the real thing?</h3>
            <p>Take a mock interview with a senior engineer and get honest, detailed feedback before the day that counts.</p>
          </div>
          <Link to="/live-classes#request-form" className="btn-hero-primary">
            Request a mock interview <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default InterviewPrep;
